/** @format */

import React, { useState, useEffect, useCallback } from "react";
import { Accordion, Form } from "react-bootstrap";
import { useLocale } from "../../../i18n/hooks";
import { useTranslation } from "react-i18next";
import { Spec, SpecItemData } from "../../../types";

type Props = {
  handleSelectedChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  SubjectId: string | undefined;
  Qualification: string | undefined;
  DefaultSelected: string;
};

// NOTE: the default selected is assumed to be correct. If problems with the selected options occur, it could be this
//        doesn't seem worth checking for it at the moment as it is complex and the DefaultSelected is computed so should theoretically be correct
const SpecAccordion: React.FC<Props> = ({
  handleSelectedChange,
  SubjectId,
  Qualification,
  DefaultSelected,
}) => {
  const locale = useLocale();
  const { t: _ } = useTranslation();

  const [rootSpecItem, setRootSpecItem] = useState<SpecItemData>();
  const [selectedItem, setSelectedItem] = useState<string>("");

  const specItemCheck = (level: string, title: string) => (
    <Form.Check
      label={`${title}`}
      name="SpecItem"
      type="radio"
      id={`specItems-${level}`}
      value={`${level}`}
      defaultChecked={level === DefaultSelected}
      data-testid={"checked-" + (level === DefaultSelected ? "here" : "no")}
      onChange={(e) => {
        handleSelectedChange(e);
        setSelectedItem(title);
      }}
    />
  );

  const getSpecItems = useCallback(async () => {
    if (Qualification !== undefined && SubjectId !== undefined) {
      try {
        let formattedQual = Qualification.replace(
          /([a-z0-9])([A-Z])/g,
          "$1-$2",
        ).toLowerCase();
        let formattedSubject = SubjectId.toLowerCase();
        const data = require(
          `../../../json/specItems/${formattedQual}-${formattedSubject}.json`,
        );
        setRootSpecItem(data);
      } catch (e) {
        setRootSpecItem(undefined);
      }
    }
  }, [Qualification, SubjectId]);

  useEffect(() => {
    getSpecItems();
  }, [SubjectId, Qualification, getSpecItems]);

  const getNextTitle = useCallback(
    (curSelected: string, specItem: Spec[]) => {
      const nextSpec = specItem[+curSelected.charAt(0)].children;
      if (nextSpec === undefined || curSelected.length === 1) {
        setSelectedItem(specItem[+curSelected].title[locale]);
        return;
      }

      getNextTitle(curSelected.substring(2, curSelected.length), nextSpec);
    },
    [locale],
  );

  useEffect(() => {
    if (rootSpecItem !== undefined && DefaultSelected !== "") {
      if (DefaultSelected.length === 1) {
        setSelectedItem(rootSpecItem.title[locale]);
      }

      getNextTitle(DefaultSelected, rootSpecItem.spec);
    }
  }, [DefaultSelected, rootSpecItem, getNextTitle, locale, selectedItem]);

  const generateAccordion = (specItem: Spec[], level: string) => {
    var activeKey: string = "";
    if (DefaultSelected !== "") {
      activeKey = DefaultSelected.substring(0, level.length + 2);
      if (activeKey.endsWith("."))
        activeKey = activeKey.substring(0, activeKey.length - 1);
    }

    return (
      <Accordion defaultActiveKey={activeKey}>
        {specItem.map((specItem, index) => {
          const _level = level ? `${level}.${index}` : `${index}`;

          return (
            <React.Fragment key={_level}>
              {specItem.children ? (
                <Accordion.Item
                  eventKey={`${_level}`}
                  data-testid={
                    _level === activeKey && activeKey !== ""
                      ? "accordion-selected"
                      : ""
                  }
                >
                  <Accordion.Header data-testid="accordion-header">
                    {
                      // add checked check here?
                    }
                    {specItemCheck(_level, specItem.title[locale])}
                  </Accordion.Header>
                  <Accordion.Body data-testid="accordion-body">
                    {generateAccordion(specItem.children, `${_level}`)}
                  </Accordion.Body>
                </Accordion.Item>
              ) : (
                specItemCheck(_level, specItem.title[locale])
              )}
            </React.Fragment>
          );
        })}
      </Accordion>
    );
  };

  return (
    <>
      {Qualification && SubjectId ? (
        rootSpecItem ? (
          <>
            <Accordion
              aria-label="Select spec item accordion"
              className="mb-4"
              data-testid="root-accord"
            >
              {generateAccordion(rootSpecItem.spec, "")}
            </Accordion>
            {selectedItem !== "" && (
              <>
                <Form.Label className="fw-bold ">
                  {_("Currently Selected Spec")}:
                </Form.Label>
                <Form.Control
                  disabled
                  className="text-truncate mb-4"
                  data-testid="selected-text"
                  value={selectedItem}
                />
              </>
            )}
          </>
        ) : (
          <p data-testid="no-spec">{_("Spec item doesn't exist")}</p>
        )
      ) : (
        <p data-testid="select-before">
          {_(
            "Please select a subject and qualification before selecting a spec item",
          )}
        </p>
      )}
    </>
  );
};

export default SpecAccordion;
